import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Mail, Lock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { BrandLogo } from '../components/BrandLogo';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { CheckoutAPI } from '../api';
import { consumePendingIntent } from '../lib/checkout';

export default function Login() {
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === 'login') {
        await login(email, password);
      } else {
        if (!name.trim()) { toast({ title: 'Please add your name' }); setBusy(false); return; }
        // New accounts must arrive here with a plan intent (from Landing/Pricing).
        // If someone opens /login directly and tries to create an account, send
        // them to /pricing to pick a subscription first.
        const intent = localStorage.getItem('shiftnotes_checkout_intent');
        if (!intent) {
          toast({ title: 'Pick a plan first', description: 'Choose a subscription to create your ShiftNotes account.' });
          navigate('/pricing');
          setBusy(false);
          return;
        }
        await register(name, email, password);
      }
      const pending = consumePendingIntent();
      if (pending) {
        try {
          const res = await CheckoutAPI.create(pending);
          if (res?.url) { window.location.href = res.url; return; }
          // No URL back — Stripe misconfigured or endpoint didn't return one.
          toast({ title: 'Checkout not available', description: 'Please try again from the pricing page.' });
          navigate('/pricing');
          return;
        } catch (err) {
          toast({ title: 'Checkout error', description: err?.response?.data?.detail || err?.message || 'Try again from the pricing page.' });
          navigate('/pricing');
          return;
        }
      }
      // No pending intent (must be an existing paid coach logging back in).
      navigate('/app');
    } catch (err) {
      toast({ title: 'Sign-in failed', description: err?.response?.data?.detail || err?.message || 'Try again.' });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center p-6">
      <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <button onClick={() => navigate('/')} className="mb-8 mx-auto block">
          <BrandLogo variant="stack" />
        </button>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 mb-6">
              <TabsTrigger value="login" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">Sign in</TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">Create account</TabsTrigger>
            </TabsList>

            <div>
              <Button type="button" onClick={handleGoogle}
                className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold">
                <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.9 0 7 1.6 8.7 3l6.4-6.4C34.9 2.3 29.9 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.5 5.8C11.9 13.3 17.4 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4.1H24v7.7h12.7c-.3 2.1-1.7 5.4-5 7.6l7.7 6c4.6-4.2 7.1-10.5 7.1-17.2z"/><path fill="#FBBC05" d="M10.1 28.3c-.5-1.5-.8-3.1-.8-4.8 0-1.7.3-3.3.8-4.8L2.6 12.9C.9 16.2 0 20 0 24s.9 7.8 2.6 11.1l7.5-6.8z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.7-6c-2.1 1.4-4.9 2.4-8.2 2.4-6.6 0-12.1-4.3-14.1-10.3l-7.5 5.8C6.5 42.6 14.6 48 24 48z"/></svg>
                Continue with Google
              </Button>
              <div className="flex items-center gap-3 my-5 text-xs text-slate-400">
                <div className="flex-1 h-px bg-slate-200" /> or <div className="flex-1 h-px bg-slate-200" />
              </div>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-3">
              {tab === 'register' && (
                <div>
                  <Label htmlFor="name" className="text-slate-700">Full name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input id="name" value={name} onChange={e => setName(e.target.value)} placeholder="Coach Rob Morgan"
                      className="pl-9" />
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@yourprogram.io" className="pl-9" />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="password" type="password" required value={password} onChange={e => setPassword(e.target.value)}
                    placeholder={tab === 'register' ? 'Choose a password (min 8 chars)' : 'Your password'}
                    minLength={tab === 'register' ? 8 : 1} className="pl-9" />
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-sky-500 hover:bg-sky-600 text-white font-semibold">
                {busy ? 'Working...' : (tab === 'login' ? 'Sign in' : 'Create account')}
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </form>
          </Tabs>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          By continuing you agree to the ShiftNotes<sup className="text-[9px] text-sky-600">&trade;</sup> Terms.  
          <Link to="/" className="text-sky-700 hover:underline ml-1">Back to homepage</Link>
        </p>
      </div>
    </div>
  );
}
